import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, ACCENTS } from '../contexts/ThemeContext';
import { prefsApi, authApi } from '../api';
import { Button, Input } from '../components/ui';

function Section({ title, description, children }) {
  const handleChangePassword = async () => {
    if (!pwd.current.trim())       { setPwdError('Current password is required'); return; }
    if (pwd.password.length < 8)   { setPwdError('New password must be at least 8 characters'); return; }
    if (pwd.password !== pwd.password_confirmation) { setPwdError('Passwords do not match'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await authApi.updateProfile({
        current_password        : pwd.current,
        password                : pwd.password,
        password_confirmation   : pwd.password_confirmation,
      });
      setPwdSaved(true);
      setPwd({ current: '', password: '', password_confirmation: '' });
      setTimeout(() => setPwdSaved(false), 2500);
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.current_password?.[0]
        || err.response?.data?.errors?.password?.[0]
        || 'Failed to update password.';
      setPwdError(msg);
    } finally { setPwdSaving(false); }
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        {description && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{description}</div>}
      </div>
      <div style={{ padding: '16px 18px' }}>{children}</div>
    </div>
  );
}

function ToggleRow({ label, sub, value, onChange, last }) {
  const handleChangePassword = async () => {
    if (!pwd.current.trim())       { setPwdError('Current password is required'); return; }
    if (pwd.password.length < 8)   { setPwdError('New password must be at least 8 characters'); return; }
    if (pwd.password !== pwd.password_confirmation) { setPwdError('Passwords do not match'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await authApi.updateProfile({
        current_password        : pwd.current,
        password                : pwd.password,
        password_confirmation   : pwd.password_confirmation,
      });
      setPwdSaved(true);
      setPwd({ current: '', password: '', password_confirmation: '' });
      setTimeout(() => setPwdSaved(false), 2500);
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.current_password?.[0]
        || err.response?.data?.errors?.password?.[0]
        || 'Failed to update password.';
      setPwdError(msg);
    } finally { setPwdSaving(false); }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 0', gap: 16,
      borderBottom: last ? 'none' : '1px solid var(--divider)',
      minHeight: 48,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
      </div>
      {/* Larger touch target for mobile */}
      <div
        onClick={() => onChange(!value)}
        style={{
          width: 44, height: 26, borderRadius: 13, cursor: 'pointer', flexShrink: 0,
          background: value ? 'var(--accent)' : 'var(--border)',
          position: 'relative', transition: 'background 0.2s',
        }}
      >
        <div style={{
          position: 'absolute', top: 3, width: 20, height: 20, borderRadius: '50%',
          background: 'white', transition: 'left 0.2s',
          left: value ? 21 : 3, boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        }} />
      </div>
    </div>
  );
}

function hexToRgbStr(hex) {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? `${parseInt(r[1],16)},${parseInt(r[2],16)},${parseInt(r[3],16)}` : '54,201,190';
}

function PasswordField({ label, value, onChange, hint }) {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value} onChange={onChange}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          placeholder="••••••••"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '11px 42px 11px 14px', fontSize: 14, fontFamily: 'inherit',
            border: `1.5px solid ${focused ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 10, background: 'var(--input-bg)', color: 'var(--text)', outline: 'none',
            boxShadow: focused ? '0 0 0 3px var(--accent-ring)' : 'none', transition: 'all 0.15s',
          }}
        />
        <button type="button" onClick={() => setShow(p => !p)}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, display: 'flex' }}>
          {show
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          }
        </button>
      </div>
      {hint && <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{hint}</p>}
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const { dark, toggleDark, accent, changeAccent } = useTheme();

  const [prefs,        setPrefs]        = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [profile,      setProfile]      = useState({ name: user?.name || '', email: user?.email || '' });
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [profileError, setProfileError] = useState('');

  // Password change state
  const [pwd,      setPwd]      = useState({ current: '', password: '', password_confirmation: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSaved,  setPwdSaved]  = useState(false);
  const [pwdError,  setPwdError]  = useState('');

  // Single fetch on mount — guard against StrictMode double-invoke
  const prefsFetchRef = useRef(false);
  useEffect(() => {
    if (prefsFetchRef.current) return;
    prefsFetchRef.current = true;
    prefsApi.get()
      .then(r => setPrefs(r.data))
      .catch(() => setPrefs({}))
      .finally(() => setPrefsLoading(false));
  }, []);

  const updatePref = async (key, val) => {
    setPrefs(p => ({ ...p, [key]: val }));
    try { await prefsApi.update({ [key]: val }); } catch {}
  };

  const handleSaveProfile = async () => {
    if (!profile.name.trim()) { setProfileError('Name is required'); return; }
    setSaving(true); setProfileError('');
    try {
      await authApi.updateProfile({ name: profile.name, email: profile.email });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.email?.[0]
        || 'Failed to save. Try again.';
      setProfileError(msg);
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!pwd.current.trim())       { setPwdError('Current password is required'); return; }
    if (pwd.password.length < 8)   { setPwdError('New password must be at least 8 characters'); return; }
    if (pwd.password !== pwd.password_confirmation) { setPwdError('Passwords do not match'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await authApi.updateProfile({
        current_password        : pwd.current,
        password                : pwd.password,
        password_confirmation   : pwd.password_confirmation,
      });
      setPwdSaved(true);
      setPwd({ current: '', password: '', password_confirmation: '' });
      setTimeout(() => setPwdSaved(false), 2500);
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.current_password?.[0]
        || err.response?.data?.errors?.password?.[0]
        || 'Failed to update password.';
      setPwdError(msg);
    } finally { setPwdSaving(false); }
  };

  return (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: 'clamp(16px,4vw,32px) clamp(12px,4vw,24px) 100px' }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Settings</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>Manage your account and preferences.</p>
      </div>

      {/* Profile */}
      <Section title="Profile" description="Your account information">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Full Name"
            value={profile.name}
            onChange={e => { setProfile(p => ({ ...p, name: e.target.value })); setProfileError(''); }}
            error={profileError}
          />
          <Input
            label="Email" type="email"
            value={profile.email}
            onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
            disabled
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Button onClick={handleSaveProfile} disabled={saving} size="sm" style={{ minHeight: 38, minWidth: 110 }}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            {saved && (
              <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                ✓ Saved
              </span>
            )}
          </div>
        </div>
      </Section>

      {/* Change Password */}
      <Section title="Password" description="Update your account password">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <PasswordField
            label="Current password"
            value={pwd.current}
            onChange={e => { setPwd(p => ({ ...p, current: e.target.value })); setPwdError(''); }}
          />
          <PasswordField
            label="New password"
            value={pwd.password}
            onChange={e => { setPwd(p => ({ ...p, password: e.target.value })); setPwdError(''); }}
            hint="At least 8 characters"
          />
          <PasswordField
            label="Confirm new password"
            value={pwd.password_confirmation}
            onChange={e => { setPwd(p => ({ ...p, password_confirmation: e.target.value })); setPwdError(''); }}
          />
          {pwdError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 13, color: '#DC2626', fontWeight: 500 }}>
              {pwdError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Button onClick={handleChangePassword} disabled={pwdSaving} size="sm" style={{ minHeight: 38, minWidth: 130 }}>
              {pwdSaving ? 'Updating…' : 'Update password'}
            </Button>
            {pwdSaved && <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 600 }}>✓ Password updated</span>}
          </div>
        </div>
      </Section>

      {/* Appearance */}
      <Section title="Appearance" description="Customise the look and feel">
        <ToggleRow
          label="Dark mode"
          sub="Switch to a dark colour scheme"
          value={dark}
          onChange={toggleDark}
        />

        <div style={{ paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Theme colour
          </div>
          {/* Grid on mobile — 3 cols; flex wrap on wider screens */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
            gap: 8,
          }}>
            {Object.entries(ACCENTS).map(([key, val]) => {
              const isActive = accent === key;
              const grad = `linear-gradient(135deg, ${val.a}, ${val.b}, ${val.c})`;
              const handleChangePassword = async () => {
    if (!pwd.current.trim())       { setPwdError('Current password is required'); return; }
    if (pwd.password.length < 8)   { setPwdError('New password must be at least 8 characters'); return; }
    if (pwd.password !== pwd.password_confirmation) { setPwdError('Passwords do not match'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await authApi.updateProfile({
        current_password        : pwd.current,
        password                : pwd.password,
        password_confirmation   : pwd.password_confirmation,
      });
      setPwdSaved(true);
      setPwd({ current: '', password: '', password_confirmation: '' });
      setTimeout(() => setPwdSaved(false), 2500);
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.current_password?.[0]
        || err.response?.data?.errors?.password?.[0]
        || 'Failed to update password.';
      setPwdError(msg);
    } finally { setPwdSaving(false); }
  };

  return (
                <button key={key} onClick={() => changeAccent(key)} title={val.name} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', borderRadius: 9, cursor: 'pointer',
                  border: `2px solid ${isActive ? val.b : 'var(--border)'}`,
                  background: isActive ? `rgba(${hexToRgbStr(val.b)},0.08)` : 'var(--hover)',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: isActive ? 700 : 500,
                  color: isActive ? val.b : 'var(--text2)',
                  transition: 'all 0.15s', minHeight: 40,
                }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: grad, display: 'inline-block', flexShrink: 0 }} />
                  {val.name}
                  {isActive && <span style={{ fontSize: 11, marginLeft: 'auto' }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Preferences */}
      {!prefsLoading && prefs && (
        <Section title="Preferences" description="Notifications and display options">
          <ToggleRow
            label="Show streak"
            sub="Display your daily completion streak"
            value={!!prefs.show_streak}
            onChange={v => updatePref('show_streak', v)}
          />
          <ToggleRow
            label="Notifications"
            sub="Browser notifications for due tasks"
            value={!!prefs.notifications_enabled}
            onChange={v => updatePref('notifications_enabled', v)}
            last
          />
        </Section>
      )}

      {/* About */}
      <Section title="About">
        {[
          ['App',      'Dayrit'],
          ['Version',  '1.0.0'],
          // ['Backend',  'Laravel + Sanctum'],
          // ['Frontend', 'React + Vite'],
        ].map(([k, v], i, arr) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--divider)' : 'none',
            minHeight: 40,
          }}>
            <span style={{ fontSize: 13, color: 'var(--text3)' }}>{k}</span>
            <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{v}</span>
          </div>
        ))}
      </Section>
    </div>
  );
}